import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import logoAsset from "@/assets/logo.svg.asset.json";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";
import { supabase } from "@/integrations/supabase/client";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

type NavLinkItem = {
  to: string;
  labelKey: string;
  sectionId?: string;
};

const NAV_OFFSET = 64; // navbar height (h-16)

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const { data: hasProjects } = useQuery({
    queryKey: ["nav-has-projects"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("id").limit(1);
      if (error) throw error;
      return data.length > 0;
    },
  });

  const { data: hasWood } = useQuery({
    queryKey: ["nav-has-wood"],
    queryFn: async () => {
      const { data, error } = await supabase.from("wood_items").select("id").limit(1);
      if (error) throw error;
      return data.length > 0;
    },
  });

  const navLinks: NavLinkItem[] = [
    { to: "/", labelKey: "nav.home", sectionId: "uvod" },
    ...(hasProjects ? [{ to: "/projekty", labelKey: "nav.projects" }] : []),
    ...(hasWood ? [{ to: "/archivni-drevo", labelKey: "nav.wood" }] : []),
    { to: "/kontakt", labelKey: "nav.contact" },
  ];

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Scroll-spy: only on home page
  useEffect(() => {
    if (!isHome) {
      setActiveSection(null);
      return;
    }

    const sectionIds = ["uvod", "o-nas", "projekty-ukazka", "kontakt-cta"];
    const triggers: ScrollTrigger[] = [];

    // Wait a tick for sections to mount
    const rafId = requestAnimationFrame(() => {
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const t = ScrollTrigger.create({
          trigger: el,
          start: `top ${NAV_OFFSET + 80}px`,
          end: "bottom 50%",
          onToggle: (self) => {
            if (self.isActive) setActiveSection(id);
          },
        });
        triggers.push(t);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      triggers.forEach((t) => t.kill());
    };
  }, [isHome]);

  // After navigating from another page to "/#aktuality", scroll to it
  useEffect(() => {
    if (isHome && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        // small delay so layout is ready
        const id2 = window.setTimeout(() => {
          gsap.to(window, {
            scrollTo: { y: el, offsetY: NAV_OFFSET },
            duration: 0.8,
            ease: "power2.inOut",
          });
        }, 80);
        return () => window.clearTimeout(id2);
      }
    }
  }, [isHome, location.hash]);

  const handleNavClick = (e: React.MouseEvent, link: NavLinkItem) => {
    setIsOpen(false);

    // In-page anchor on home
    if (link.sectionId && isHome) {
      e.preventDefault();
      const el = document.getElementById(link.sectionId);
      if (el) {
        gsap.to(window, {
          scrollTo: { y: el, offsetY: NAV_OFFSET },
          duration: 0.8,
          ease: "power2.inOut",
        });
        // update hash without jump
        history.replaceState(null, "", link.to);
        setActiveSection(link.sectionId);
      }
      return;
    }

    // Cross-page anchor (e.g. /#aktuality from /projekty)
    if (link.sectionId && !isHome && link.to.includes("#")) {
      e.preventDefault();
      navigate(link.to);
    }
  };

  const isLinkActive = (link: NavLinkItem) => {
    if (link.sectionId && isHome) {
      return activeSection === link.sectionId;
    }
    return location.pathname === link.to.split("#")[0] && !link.to.includes("#");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="container mx-auto px-4 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" onClick={(e) => handleNavClick(e, navLinks[0])}>
          <img src={logoAsset.url} alt="Umělecké Tesařství" className="h-10 w-auto dark:brightness-0 dark:invert" width={40} height={40} />
          <span className="font-serif text-xl md:text-2xl text-primary leading-tight tracking-[0.08em] uppercase font-normal">Umělecké Tesařství</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => {
            const active = isLinkActive(link);
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => handleNavClick(e, link)}
                className={`relative text-sm font-medium transition-colors hover:text-primary py-1 ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {t(link.labelKey)}
                {active && (
                  <motion.span
                    layoutId="navbar-underline"
                    className="absolute left-0 right-0 -bottom-0.5 h-0.5 bg-accent rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
          <LanguageSwitcher />
          <ThemeToggle />
        </div>

        {/* Mobile: language switcher + toggle */}
        <div className="flex items-center gap-1 md:hidden">
          <LanguageSwitcher compact />
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isOpen ? "close" : "open"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center"
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </motion.span>
            </AnimatePresence>
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="md:hidden overflow-hidden border-b border-border bg-background"
          >
            <div className="px-4 pb-4 pt-1">
              {navLinks.map((link, i) => {
                const active = isLinkActive(link);
                return (
                  <motion.div
                    key={link.to}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.05, duration: 0.25 }}
                  >
                    <Link
                      to={link.to}
                      onClick={(e) => handleNavClick(e, link)}
                      className={`block py-3 text-sm font-medium transition-colors hover:text-primary ${
                        active ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {t(link.labelKey)}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
